import React, { useEffect, useState } from 'react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import useReportApi from 'app/hooks/useReportApi';
import { MainLoader } from 'app/components/SkeletonLoader/MainLoader';
import styled from 'styled-components';

// Type definitions
export interface PowerBIConfig {
  REPORT_ID_FACILITY: string;
  REPORT_ID_LOAN: string;
  REPORT_ID_GUARANTEE: string;
  WORKSPACE_ID: string;
  CLIENT_ID: string;
  TENANT_ID: string;
}
// Static configuration values
export const CONFIG: PowerBIConfig = {
  REPORT_ID_FACILITY: '57d66ebe-cb2d-4891-8a77-9afc2b2db12b',
  REPORT_ID_LOAN: '8397b86a-59e7-4447-840c-cb213adf107f',
  REPORT_ID_GUARANTEE: '3a5c7e0a-2776-42a4-9beb-e040a4370d91',
  WORKSPACE_ID: 'c780f917-7550-4222-92b5-21efdaebc151',
  CLIENT_ID: '5288b226-fcd9-4a42-a0f6-7e4852cbe787',
  TENANT_ID: 'd0368d3a-beef-4c0a-b6a3-b390f4aa4b5a',
};
declare global {
  interface Window {
    report: any; // Adjust the type if you know the exact type of `embeddedReport`
  }
}
type Props = {
  pageType: 'facility' | 'loan' | 'guarantee';
};
const ReportPage = ({ pageType }: Props) => {
  const [tokenData, setTokenData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reportId =
    pageType === 'facility'
      ? CONFIG.REPORT_ID_FACILITY
      : pageType === 'loan'
      ? CONFIG.REPORT_ID_LOAN
      : CONFIG.REPORT_ID_GUARANTEE;

  const { useMasterDataList } = useReportApi();
  const {
    data: reportData,
    isFetching,
    error: apiError,
  } = useMasterDataList(reportId, CONFIG.WORKSPACE_ID);

  // Embed configuration for PowerBIEmbed
  const embedConfig = {
    type: 'report', // Supported types: report, dashboard, tile, visual, and qna.
    id: reportId,
    embedUrl: `https://app.powerbi.com/reportEmbed?reportId=${reportId}&groupId=${CONFIG.WORKSPACE_ID}`,
    accessToken: tokenData || '', // Token fetched dynamically
    tokenType: models.TokenType.Embed, // Use models.TokenType.Aad if you're embedding for your organization
    settings: {
      layoutType:
        window.innerWidth < 768
          ? models.LayoutType.MobilePortrait
          : models.LayoutType.Master,
      panes: {
        filters: {
          expanded: false,
          visible: true,
        },
      },
    },
  };

  // Event handlers for Power BI events
  const eventHandlers = new Map([
    ['loaded', () => {}],
    ['rendered', () => {}],
    [
      'error',
      (event: any) => {
        console.error('Error embedding report:', event.detail);
      },
    ],
  ]);

  const getEmbeddedComponent = (embeddedReport: any) => {
    window.report = embeddedReport; // Optional: Assign the embedded report for further use
  };

  useEffect(() => {
    if (reportData) {
      const {
        embedToken: { token },
      } = reportData;
      setTokenData(token);
      setError(null); // Clear previous errors on successful token fetch
    }
    if (apiError) {
      setError(apiError.message || 'Failed to fetch Power BI token');
    }
  }, [reportData, apiError]);

  if (error) {
    return (
      <div className="w-full rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="p-4">
          <div className="flex h-96 items-center justify-center text-red-600">
            <p>Error: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="p-4">
        {isFetching ? (
          <MainLoader />
        ) : (
          <Wrapper>
            <PowerBIEmbed
              embedConfig={embedConfig}
              eventHandlers={eventHandlers}
              cssClassName="w-full h-[90vh] max-w-[2000px] report-style-class"
              getEmbeddedComponent={getEmbeddedComponent}
            />
          </Wrapper>
        )}
      </div>
    </div>
  );
};

export default ReportPage;

const Wrapper = styled.div`
  .report-style-class {
    height: calc(100vh - 80px);
  }
`;
